import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Store, Building2, User, FileText, ShieldCheck, Clock, XCircle, Upload, ImageIcon, Briefcase, Palette, Receipt, Pencil, CheckCircle2, Sparkles, CalendarClock, UtensilsCrossed, ArrowRight } from 'lucide-react';
import { ShopDocumentsManager } from '../components/ShopDocuments';
import PremiumCustomizer from '../components/PremiumCustomizer';
import AccountingDocuments from '../components/AccountingDocuments';
import ComplianceWidget from '../components/ComplianceWidget';
import WorkingHoursPicker from '../components/WorkingHoursPicker';
import CustomDomainSection from '../components/CustomDomainSection';
import LocationPicker from '../components/LocationPicker';
import { MapPin } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_ICONS = {
  sia: Building2,
  ik: FileText,
  person: User
};

export default function ShopProfilePage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    shop_name: '',
    legal_name: '',
    entity_type: '',
    registration_number: '',
    vat_number: '',
    default_vat_rate: 21,
    legal_address: '',
    bank_name: '',
    bank_account: '',
    shop_description: '',
    shop_logo_url: '',
    shop_cover_url: '',
    shop_phone: '',
    shop_working_hours: '',
    shop_languages: [],
    shop_facebook: '',
    shop_instagram: '',
    shop_tiktok: '',
    shop_youtube: '',
    shop_whatsapp: '',
    region: '',
    location_id: '',
    location_name: '',
    location_region_slug: '',
    location_district: '',
    location_district_slug: '',
    lat: null,
    lng: null
  });
  const [districts, setDistricts] = useState([]);
  const [autofillNote, setAutofillNote] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [requestingVerification, setRequestingVerification] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [registryCheck, setRegistryCheck] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [pauseUntil, setPauseUntil] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchShopProfile();
    fetchVerificationStatus();
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchShopProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/shop-profile`);
      if (data) {
        setFormData({
          shop_name: data.shop_name || '',
          legal_name: data.legal_name || '',
          entity_type: data.entity_type || '',
          registration_number: data.registration_number || '',
          vat_number: data.vat_number || '',
          default_vat_rate: data.default_vat_rate ?? 21,
          legal_address: data.legal_address || '',
          bank_name: data.bank_name || '',
          bank_account: data.bank_account || '',
          shop_description: data.shop_description || '',
          shop_logo_url: data.shop_logo_url || '',
          shop_cover_url: data.shop_cover_url || '',
          shop_phone: data.shop_phone || '',
          shop_working_hours: data.shop_working_hours || '',
          shop_languages: data.shop_languages || [],
          shop_facebook: data.shop_facebook || '',
          shop_instagram: data.shop_instagram || '',
          shop_tiktok: data.shop_tiktok || '',
          shop_youtube: data.shop_youtube || '',
          shop_whatsapp: data.shop_whatsapp || '',
          region: data.region || '',
          location_id: data.location_id || '',
          location_name: data.location_name || '',
          location_region_slug: data.location_region_slug || '',
          location_district: data.location_district || '',
          location_district_slug: data.location_district_slug || '',
          lat: data.lat ?? null,
          lng: data.lng ?? null
        });
        setIsEditMode(true);
        setIsPaused(!!data.is_paused);
        setPauseUntil((data.pause_until || '').slice(0, 10));
        if (data.lat != null && data.lng != null) fetchDistrictsNear(data.lat, data.lng);
      }
    } catch (error) {
      // No profile yet
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/verification/status`);
      setVerificationStatus(data);
      if (!data.is_verified) {
        try {
          const { data: rc } = await axios.get(`${API}/verification/registry-check`);
          setRegistryCheck(rc);
        } catch (e) { setRegistryCheck(null); }
      }
    } catch (e) { /* not yet created */ }
  };

  const handleRequestVerification = async () => {
    setRequestingVerification(true);
    setError('');
    try {
      await axios.post(`${API}/verification/request`);
      fetchVerificationStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error requesting verification');
    } finally {
      setRequestingVerification(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const fetchDistrictsNear = async (lat, lng) => {
    if (lat == null || lng == null) { setDistricts([]); return; }
    try {
      const { data } = await axios.get(`${API}/locations/districts-near`, { params: { lat, lng, radius_km: 12 } });
      setDistricts(data.districts || []);
    } catch (e) { setDistricts([]); }
  };

  const handleLocationSelect = async (l) => {
    setFormData((prev) => ({
      ...prev,
      location_id: l.id || '',
      location_name: l.name,
      location_region_slug: l.region_slug || prev.location_region_slug,
      location_district: '',
      location_district_slug: '',
      lat: l.lat,
      lng: l.lng,
    }));
    setDistricts([]);
    try {
      const { data } = await axios.get(`${API}/locations/resolve-region`, { params: { id: l.id, slug: l.slug } });
      if (data.city_slug) {
        setFormData((prev) => ({ ...prev, region: data.city_slug }));
        setAutofillNote(
          language === 'lv'
            ? `Automātiski aizpildīts: ${data.city_name || data.city_slug}. Vari mainīt, ja nepieciešams.`
            : `Auto-filled: ${data.city_name || data.city_slug}. You can change it if needed.`
        );
      } else {
        setAutofillNote('');
      }
      fetchDistrictsNear(data.city_lat ?? l.lat, data.city_lng ?? l.lng);
    } catch (e) { /* best-effort */ }
  };

  const handleDistrictSelect = (slug) => {
    const d = districts.find((x) => x.slug === slug);
    setFormData((prev) => ({ ...prev, location_district: d ? d.name : '', location_district_slug: slug }));
  };

  const [editRequestPending, setEditRequestPending] = useState(false);
  const [legalEditUnlocked, setLegalEditUnlocked] = useState(false);
  const [editRequestFields, setEditRequestFields] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    setEditRequestPending(false);

    try {
      if (isEditMode) {
        const { data } = await axios.put(`${API}/shop-profile`, formData);
        if (data._edit_request_pending) {
          setEditRequestPending(true);
          setEditRequestFields(data._edit_request_fields || []);
          setLegalEditUnlocked(false);
        } else {
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
        }
      } else {
        await axios.post(`${API}/shop-profile`, formData);
        setIsEditMode(true);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError(language === 'lv' ? 'Kļūda saglabājot' : 'Error saving');
      }
    } finally {
      setSaving(false);
    }
  };

  const entityTypes = [
    { value: 'sia', label: t('sia'), icon: Building2, desc: language === 'lv' ? 'Sabiedrība ar ierobežotu atbildību' : 'Limited liability company' },
    { value: 'ik', label: t('ik'), icon: FileText, desc: language === 'lv' ? 'Individuālais komersants' : 'Sole proprietorship' },
    { value: 'person', label: t('person'), icon: User, desc: language === 'lv' ? 'Fiziska persona' : 'Natural person' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]"></div>
      </div>
    );
  }

  const SECTIONS = [
    { id: 'profile', icon: Store, lv: 'Profils un zīmols', en: 'Profile & Branding' },
    { id: 'legal', icon: Briefcase, lv: 'Bizness un juridiskie dati', en: 'Business & Legal' },
    { id: 'design', icon: Palette, lv: 'Premium dizains', en: 'Premium Design' },
    { id: 'accounting', icon: Receipt, lv: 'Grāmatvedība', en: 'Accounting' },
  ];

  // After verification, admin-protected legal fields are read-only until the
  // seller explicitly unlocks them; saving then routes through admin approval.
  const legalLocked = !!verificationStatus?.is_verified && !legalEditUnlocked;
  const lockCls = legalLocked ? ' opacity-60 cursor-not-allowed !bg-[#F3F2EE]' : '';

  const togglePause = async () => {
    const next = !isPaused;
    setPauseBusy(true);
    try {
      await axios.put(`${API}/shop-profile/pause`, { paused: next, pause_until: next ? (pauseUntil || null) : null });
      setIsPaused(next);
      if (!next) setPauseUntil('');
      toast.success(next
        ? (language === 'lv' ? 'Veikals pauzēts — paslēpts no kartes un marketplace' : 'Shop paused — hidden from map and marketplace')
        : (language === 'lv' ? 'Veikals atkal aktīvs' : 'Shop is active again'));
    } catch (e) {
      toast.error(language === 'lv' ? 'Neizdevās mainīt statusu' : 'Failed to update status');
    } finally {
      setPauseBusy(false);
    }
  };

  const saveSchedule = async (date) => {
    setPauseUntil(date);
    if (!isPaused) return;
    try {
      await axios.put(`${API}/shop-profile/pause`, { paused: true, pause_until: date || null });
      toast.success(date
        ? (language === 'lv' ? `Atsāksies automātiski ${date}` : `Will auto-resume on ${date}`)
        : (language === 'lv' ? 'Pauze bez termiņa' : 'Pause with no end date'));
    } catch (e) { toast.error(language === 'lv' ? 'Neizdevās saglabāt datumu' : 'Failed to save date'); }
  };

  // Section completeness for the nav badges (helps activation overview).
  const sectionComplete = {
    profile: !!formData.shop_name && !!formData.entity_type,
    legal: formData.entity_type === 'private'
      ? true
      : (!!formData.legal_name && !!formData.registration_number && !!formData.legal_address),
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="shop-profile-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'lv' ? 'Atpakaļ' : 'Back'}</span>
        </button>

        {/* Shop pause toggle — hides the shop from the map + marketplace */}
        {isEditMode && (
          <div
            className={`mb-6 p-4 sm:p-5 rounded-2xl border flex items-center gap-4 flex-wrap ${isPaused ? 'bg-amber-50 border-amber-300' : 'bg-white border-[#E5E4E0]'}`}
            data-testid="shop-pause-card"
          >
            <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center ${isPaused ? 'bg-amber-500' : 'bg-[#4A6B53]'}`}>
              {isPaused ? <Clock className="w-5 h-5 text-white" /> : <Store className="w-5 h-5 text-white" />}
            </div>
            <div className="flex-1 min-w-[220px]">
              <p className="font-semibold text-[#121212] text-sm sm:text-base">
                {isPaused
                  ? (language === 'lv' ? 'Veikals ir pauzēts' : 'Shop is paused')
                  : (language === 'lv' ? 'Veikals ir aktīvs' : 'Shop is active')}
              </p>
              <p className="text-xs sm:text-sm text-[#5C5C5C] mt-0.5">
                {isPaused
                  ? (language === 'lv' ? 'Paslēpts no kartes un marketplace. Sludinājumi nav redzami pircējiem.' : 'Hidden from the map and marketplace. Listings are not visible to buyers.')
                  : (language === 'lv' ? 'Pauzē, lai īslaicīgi paslēptu veikalu no kartes un marketplace.' : 'Pause to temporarily hide the shop from the map and marketplace.')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#5C5C5C]">{language === 'lv' ? 'Pauzēt' : 'Pause'}</span>
              <Switch checked={isPaused} onCheckedChange={togglePause} disabled={pauseBusy} data-testid="shop-pause-toggle" />
            </div>
            {isPaused && (
              <div className="w-full flex items-center gap-3 flex-wrap pt-3 mt-1 border-t border-amber-200" data-testid="shop-pause-schedule">
                <span className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Atsākt automātiski:' : 'Auto-resume on:'}</span>
                <input
                  type="date"
                  value={pauseUntil}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => saveSchedule(e.target.value)}
                  className="text-sm border border-[#E5E4E0] rounded-lg px-3 py-1.5 bg-white"
                  data-testid="shop-pause-until"
                />
                {pauseUntil && (
                  <button onClick={() => saveSchedule('')} className="text-xs text-[#5C5C5C] underline" data-testid="shop-pause-clear-date">
                    {language === 'lv' ? 'Noņemt termiņu' : 'Clear date'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Onboarding CTA — only shown when the user hasn't yet completed verification.
            Encourages the guided 4-step path; the full ShopProfilePage form remains
            available for power users who want to edit fields directly. */}
        {!verificationStatus?.is_verified && (
          <div
            className="mb-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-[#D97736]/10 via-[#F0A05A]/5 to-transparent border border-[#D97736]/30 flex items-start sm:items-center gap-4 flex-wrap"
            data-testid="onboarding-cta-banner"
          >
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-[#D97736] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="font-semibold text-[#121212] text-sm sm:text-base">
                {language === 'lv'
                  ? 'Vēlies bezmaksas kataloga piekļuvi?'
                  : 'Want free catalog access?'}
              </p>
              <p className="text-xs sm:text-sm text-[#5C5C5C] mt-0.5">
                {language === 'lv'
                  ? 'Izej cauri 4 soļiem — info, rekvizīti, dokumenti, verifikācija — un saņem bezmaksas piekļuvi visiem katalogiem.'
                  : 'Go through 4 steps — info, requisites, documents, verification — and get free access to all catalogs.'}
              </p>
            </div>
            <Button
              onClick={() => navigate('/shop-onboarding')}
              className="bg-[#D97736] hover:bg-[#C06528] text-white"
              data-testid="onboarding-cta-button"
            >
              {language === 'lv' ? 'Sākt soli pa solim →' : 'Start guided setup →'}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Section navigation — left rail on desktop, horizontal scroll on mobile */}
          <aside className="lg:col-span-3 lg:sticky lg:top-8">
            <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="shop-profile-nav">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveSection(s.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap lg:whitespace-normal ${isActive ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'text-[#5C5C5C] hover:bg-[#E5E4E0]/40 hover:text-[#121212]'}`}
                    data-testid={`nav-${s.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{language === 'lv' ? s.lv : s.en}</span>
                    {isEditMode && sectionComplete[s.id] === true && (
                      <CheckCircle2 className="w-4 h-4 text-[#4A6B53] shrink-0" data-testid={`nav-complete-${s.id}`} />
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Section content */}
          <div className="lg:col-span-9 space-y-6 min-w-0">
            {(activeSection === 'profile' || activeSection === 'legal') && (
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#D97736] flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-outfit text-2xl font-bold text-[#121212]">
                  {isEditMode ? t('editShop') : t('createShop')}
                </h1>
                <p className="text-sm text-[#5C5C5C]">
                {language === 'lv' ? 'Aizpildiet anketu, lai izveidotu savu veikalu' : 'Fill in the form to create your shop'}
              </p>
              </div>
            </div>
            {isEditMode && (
              <button
                type="button"
                onClick={() => navigate('/shop-collections')}
                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white transition-colors"
                data-testid="manage-collections-link"
              >
                {language === 'lv' ? 'Pārvaldīt kolekcijas →' : 'Manage collections →'}
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="error-message">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm" data-testid="success-message">
                {language === 'lv' ? 'Veikals saglabāts!' : 'Shop saved!'}
              </div>
            )}

            {editRequestPending && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm" data-testid="edit-request-pending">
                <p className="font-medium mb-1">
                  {language === 'lv' ? 'Izmaiņas gaida administratora apstiprinājumu' : 'Changes pending admin approval'}
                </p>
                <p className="text-xs text-amber-600">
                  {language === 'lv'
                    ? `Lauki: ${editRequestFields.join(', ')}. Pārējās izmaiņas saglabātas uzreiz.`
                    : `Fields: ${editRequestFields.join(', ')}. Other changes saved immediately.`}
                </p>
              </div>
            )}

            {/* Logo & Cover Upload */}
            <div className={activeSection === 'profile' ? 'space-y-4' : 'hidden'}>
              <Label className="text-[#121212] font-medium flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-[#D97736]" />
                {language === 'lv' ? 'Logo un vāka attēls' : 'Logo & Cover Image'}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Logo */}
                <div className="space-y-2">
                  <span className="text-sm text-[#5C5C5C]">Logo</span>
                  <div className="relative w-24 h-24 rounded-2xl border-2 border-dashed border-[#E5E4E0] overflow-hidden group">
                    {formData.shop_logo_url ? (
                      <>
                        <img src={resolveAssetUrl(formData.shop_logo_url)} alt="Logo" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setFormData(p => ({ ...p, shop_logo_url: '' }))}
                          className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <XCircle className="w-6 h-6 text-white" />
                        </button>
                      </>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-[#F3F2EE] transition-colors">
                        <Upload className="w-5 h-5 text-[#5C5C5C] mb-1" />
                        <span className="text-[10px] text-[#5C5C5C]">{uploadingLogo ? '...' : 'Upload'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={uploadingLogo}
                          data-testid="logo-upload-input"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingLogo(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              setFormData(p => ({ ...p, shop_logo_url: data.url }));
                            } catch (err) { console.error(err); }
                            finally { setUploadingLogo(false); e.target.value = ''; }
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
                {/* Cover */}
                <div className="space-y-2">
                  <span className="text-sm text-[#5C5C5C]">{language === 'lv' ? 'Vāka attēls' : 'Cover'}</span>
                  <div className="relative w-full h-24 rounded-2xl border-2 border-dashed border-[#E5E4E0] overflow-hidden group">
                    {formData.shop_cover_url ? (
                      <>
                        <img src={resolveAssetUrl(formData.shop_cover_url)} alt="Cover" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setFormData(p => ({ ...p, shop_cover_url: '' }))}
                          className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <XCircle className="w-6 h-6 text-white" />
                        </button>
                      </>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-[#F3F2EE] transition-colors">
                        <Upload className="w-5 h-5 text-[#5C5C5C] mb-1" />
                        <span className="text-xs text-[#5C5C5C]">{uploadingCover ? '...' : (language === 'lv' ? 'Augšupielādēt vāku' : 'Upload cover')}</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={uploadingCover}
                          data-testid="cover-upload-input"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingCover(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              setFormData(p => ({ ...p, shop_cover_url: data.url }));
                            } catch (err) { console.error(err); }
                            finally { setUploadingCover(false); e.target.value = ''; }
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={activeSection === 'profile' ? 'space-y-6' : 'hidden'}>
            {/* Basic Info Section */}
            <div className="pt-2">
              <h3 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
                <Store className="w-5 h-5 text-[#D97736]" />
                {language === 'lv' ? 'Pamatinformācija' : 'Basic information'}
              </h3>
            </div>

            {/* Shop Name */}
            <div className="space-y-2">
              <Label htmlFor="shop_name" className="text-[#121212] font-medium">
                {t('shopName')} *
              </Label>
              <Input
                id="shop_name"
                name="shop_name"
                value={formData.shop_name}
                onChange={handleChange}
                required
                disabled={legalLocked}
                className={`h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]${lockCls}`}
                placeholder={language === 'lv' ? 'Jūsu veikala nosaukums' : 'Your shop name'}
                data-testid="shop-name-input"
              />
              {legalLocked && (
                <p className="text-xs text-[#9CA3AF]" data-testid="shop-name-locked-hint">
                  {language === 'lv' ? '🔒 Aizsargāts lauks — labojams sadaļā "Bizness un juridiskie dati"' : '🔒 Protected — edit under "Business & Legal"'}
                </p>
              )}
            </div>

            {/* Shop Description (moved into Basic Info) */}
            <div className="space-y-2">
              <Label htmlFor="shop_description" className="text-[#121212] font-medium">
                {t('shopDescription')}
              </Label>
              <Textarea
                id="shop_description"
                name="shop_description"
                value={formData.shop_description}
                onChange={handleChange}
                rows={4}
                className="bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736] resize-none"
                placeholder={language === 'lv' ? 'Pastāstiet par savu veikalu...' : 'Tell us about your shop...'}
                data-testid="shop-description-input"
              />
            </div>

            {/* Shop Location — listings inherit this */}
            <div className="space-y-3 border-t border-[#E5E4E0] pt-4" data-testid="shop-location-block">
              <div>
                <Label className="text-[#121212] font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-[#D97736]" /> {language === 'lv' ? 'Veikala atrašanās vieta' : 'Shop location'}
                </Label>
                <p className="text-xs text-[#5C5C5C] mt-1">
                  {language === 'lv'
                    ? 'Šī atrašanās vieta tiks automātiski piemērota visiem Jūsu sludinājumiem.'
                    : 'This location is automatically applied to all your listings.'}
                </p>
              </div>
              <LocationPicker value={formData.location_name}
                onSelect={handleLocationSelect}
                placeholder={language === 'lv' ? 'Meklē pilsētu vai ciemu...' : 'Search city or village...'}
                testid="shop-location-picker" />
              {autofillNote && (
                <p className="text-xs text-[#4A6B53] flex items-center gap-1" data-testid="shop-location-autofill-note">
                  <MapPin className="w-3 h-3" /> {autofillNote}
                </p>
              )}
              {districts.length > 0 && (
                <div className="space-y-2" data-testid="shop-microdistrict-block">
                  <Label className="text-[#121212] font-medium flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {language === 'lv' ? 'Mikrorajons (neobligāts)' : 'Microdistrict (optional)'}
                  </Label>
                  <Select value={formData.location_district_slug || ''} onValueChange={handleDistrictSelect}>
                    <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="shop-microdistrict-select">
                      <SelectValue placeholder={language === 'lv' ? 'Izvēlies tuvāko mikrorajonu...' : 'Select nearest microdistrict...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map((d) => (
                        <SelectItem key={d.slug} value={d.slug} data-testid={`shop-microdistrict-option-${d.slug}`}>
                          {d.name} <span className="text-[#9CA3AF]">· {d.distance_km} km</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#5C5C5C]">
                    {language === 'lv' ? 'Sakārtoti pēc attāluma no pilsētas centra.' : 'Sorted by distance from the city centre.'}
                  </p>
                </div>
              )}
            </div>

            {/* Entity Type Selection */}
            <div className="space-y-3">
              <Label className="text-[#121212] font-medium">
                {t('entityType')} *
              </Label>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {entityTypes.map((entity) => {
                  const Icon = entity.icon;
                  const isSelected = formData.entity_type === entity.value;
                  return (
                    <button
                      key={entity.value}
                      type="button"
                      disabled={legalLocked}
                      onClick={() => !legalLocked && setFormData(prev => ({ ...prev, entity_type: entity.value }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? 'border-[#D97736] bg-[#D97736]/5'
                          : 'border-[#E5E4E0] hover:border-[#D97736]/50'
                      }${legalLocked ? ' opacity-60 cursor-not-allowed' : ''}`}
                      data-testid={`entity-type-${entity.value}`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-[#D97736]' : 'text-[#5C5C5C]'}`} />
                      <div className={`font-semibold text-sm ${isSelected ? 'text-[#D97736]' : 'text-[#121212]'}`}>
                        {entity.label}
                      </div>
                      <div className="text-xs text-[#5C5C5C] mt-1">{entity.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            </div>

            {/* Requisites Section */}
            <div className={activeSection === 'legal' ? 'border-t border-[#E5E4E0] pt-6' : 'hidden'}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="font-outfit font-semibold text-[#121212] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#D97736]" />
                  {t('requisites')}
                </h3>
                {verificationStatus?.is_verified && (
                  legalLocked ? (
                    <button type="button" onClick={() => setLegalEditUnlocked(true)}
                      className="flex items-center gap-1.5 text-xs font-medium text-[#D97736] hover:text-[#C06528] shrink-0"
                      data-testid="unlock-legal-btn">
                      <Pencil className="w-3.5 h-3.5" /> {language === 'lv' ? 'Labot' : 'Edit'}
                    </button>
                  ) : (
                    <button type="button" onClick={() => setLegalEditUnlocked(false)}
                      className="text-xs font-medium text-[#5C5C5C] hover:text-[#121212] shrink-0"
                      data-testid="cancel-legal-edit-btn">
                      {language === 'lv' ? 'Atcelt' : 'Cancel'}
                    </button>
                  )
                )}
              </div>

              {verificationStatus?.is_verified && (
                <div className={`mb-4 p-3 rounded-xl border text-xs flex items-start gap-2 ${legalLocked ? 'bg-[#FAFAFA] border-[#F3F2EE] text-[#5C5C5C]' : 'bg-[#D97736]/5 border-[#D97736]/30 text-[#9a4f1d]'}`} data-testid="legal-lock-note">
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-[#4A6B53]" />
                  {legalLocked
                    ? (language === 'lv' ? 'Veikals ir verificēts. Juridiskie dati ir bloķēti. Nospied "Labot", lai pieprasītu izmaiņas.' : 'Shop is verified. Legal details are locked. Click "Edit" to request changes.')
                    : (language === 'lv' ? 'Izmaiņas juridiskajos datos tiks nosūtītas administratoram apstiprināšanai pirms publicēšanas.' : 'Changes to legal details will be sent to an admin for approval before going live.')}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Legal Name — first field in requisites */}
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="legal_name" className="text-[#121212] font-medium">
                    {language === 'lv' ? 'Pilnais juridiskais nosaukums' : 'Full Legal Name'}
                  </Label>
                  <Input
                    id="legal_name"
                    name="legal_name"
                    value={formData.legal_name}
                    onChange={handleChange}
                    disabled={legalLocked}
                    className={`h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]${lockCls}`}
                    placeholder={language === 'lv' ? 'Uzņēmuma nosaukums' : 'Company name'}
                    data-testid="legal-name-input"
                  />
                </div>

                {(formData.entity_type === 'sia' || formData.entity_type === 'ik') && (
                  <div className="space-y-2">
                    <Label htmlFor="registration_number" className="text-[#121212] font-medium">
                      {t('registrationNumber')}
                    </Label>
                    <Input
                      id="registration_number"
                      name="registration_number"
                      value={formData.registration_number}
                      onChange={handleChange}
                      disabled={legalLocked}
                      className={`h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]${lockCls}`}
                      placeholder="40000000000"
                      data-testid="reg-number-input"
                    />
                  </div>
                )}

                {(formData.entity_type === 'sia' || formData.entity_type === 'ik') && (
                  <div className="space-y-2">
                    <Label htmlFor="vat_number" className="text-[#121212] font-medium">
                      {t('vatNumber')}
                    </Label>
                    <Input
                      id="vat_number"
                      name="vat_number"
                      value={formData.vat_number}
                      onChange={handleChange}
                      disabled={legalLocked}
                      className={`h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]${lockCls}`}
                      placeholder="LV40000000000"
                      data-testid="vat-number-input"
                    />
                    <p className="text-xs text-[#5C5C5C]">
                      {language === 'lv' ? 'Aizpildi tikai, ja esi PVN maksātājs.' : language === 'ru' ? 'Заполняйте, только если вы плательщик НДС.' : 'Fill only if you are VAT-registered.'}
                    </p>
                  </div>
                )}

                {(formData.entity_type === 'sia' || formData.entity_type === 'ik') && formData.vat_number && (
                  <div className="space-y-2">
                    <Label htmlFor="default_vat_rate" className="text-[#121212] font-medium">
                      {language === 'lv' ? 'PVN likme (noklusējuma)' : language === 'ru' ? 'Ставка НДС (по умолчанию)' : 'VAT rate (default)'}
                    </Label>
                    <Select
                      value={String(formData.default_vat_rate ?? 21)}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, default_vat_rate: parseInt(v, 10) }))}
                    >
                      <SelectTrigger className="h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="vat-rate-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="21" data-testid="vat-rate-option-21">21% ({language === 'lv' ? 'standarta' : language === 'ru' ? 'стандартная' : 'standard'})</SelectItem>
                        <SelectItem value="12" data-testid="vat-rate-option-12">12% ({language === 'lv' ? 'samazināta' : language === 'ru' ? 'сниженная' : 'reduced'})</SelectItem>
                        <SelectItem value="5" data-testid="vat-rate-option-5">5% ({language === 'lv' ? 'samazināta' : language === 'ru' ? 'сниженная' : 'reduced'})</SelectItem>
                        <SelectItem value="0" data-testid="vat-rate-option-0">0% ({language === 'lv' ? 'eksports' : language === 'ru' ? 'экспорт' : 'export'})</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#5C5C5C]">
                      {language === 'lv'
                        ? 'Šī likme tiks piemērota pavadzīmēs. Katrā sludinājumā to var pārrakstīt.'
                        : 'Applied on invoices. Can be overridden per listing.'}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="legal_address" className="text-[#121212] font-medium">
                    {t('legalAddress')}
                  </Label>
                  <Input
                    id="legal_address"
                    name="legal_address"
                    value={formData.legal_address}
                    onChange={handleChange}
                    disabled={legalLocked}
                    className={`h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]${lockCls}`}
                    placeholder={language === 'lv' ? 'No reģistra' : 'From registry'}
                    data-testid="legal-address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_name" className="text-[#121212] font-medium">
                    {t('bankName')}
                  </Label>
                  <Input
                    id="bank_name"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="Swedbank"
                    data-testid="bank-name-input"
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="bank_account" className="text-[#121212] font-medium">
                    {t('bankAccount')}
                  </Label>
                  <Input
                    id="bank_account"
                    name="bank_account"
                    value={formData.bank_account}
                    onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="LV00HABA0000000000000"
                    data-testid="bank-account-input"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info Section */}
            <div className={activeSection === 'profile' ? 'border-t border-[#E5E4E0] pt-6' : 'hidden'}>
              <h3 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#D97736]" />
                {language === 'lv' ? 'Kontaktinformācija' : 'Contact information'}
              </h3>

              {/* Shop Phone */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="shop_phone" className="text-[#121212] font-medium">
                  {language === 'lv' ? 'Veikala telefons' : 'Shop Phone'}
                </Label>
                <Input
                  id="shop_phone"
                  name="shop_phone"
                  type="tel"
                  value={formData.shop_phone}
                  onChange={handleChange}
                  className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                  placeholder="+371 20000000"
                  data-testid="shop-phone-input"
                />
              </div>

              {/* Social links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="shop_facebook" className="text-[#121212] font-medium">Facebook</Label>
                  <Input
                    id="shop_facebook"
                    name="shop_facebook"
                    value={formData.shop_facebook}
                    onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="facebook.com/tavsveikals"
                    data-testid="shop-facebook-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shop_instagram" className="text-[#121212] font-medium">Instagram</Label>
                  <Input
                    id="shop_instagram"
                    name="shop_instagram"
                    value={formData.shop_instagram}
                    onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="instagram.com/tavsveikals"
                    data-testid="shop-instagram-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shop_tiktok" className="text-[#121212] font-medium">TikTok</Label>
                  <Input id="shop_tiktok" name="shop_tiktok" value={formData.shop_tiktok} onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="tiktok.com/@tavsveikals" data-testid="shop-tiktok-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shop_youtube" className="text-[#121212] font-medium">YouTube</Label>
                  <Input id="shop_youtube" name="shop_youtube" value={formData.shop_youtube} onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="youtube.com/@tavsveikals" data-testid="shop-youtube-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shop_whatsapp" className="text-[#121212] font-medium">WhatsApp</Label>
                  <Input id="shop_whatsapp" name="shop_whatsapp" value={formData.shop_whatsapp} onChange={handleChange}
                    className="h-11 bg-[#FAFAFA] border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                    placeholder="+371 2xxxxxxx" data-testid="shop-whatsapp-input" />
                </div>
              </div>

              {/* Languages */}
              <div className="space-y-2">
                <Label className="text-[#121212] font-medium">
                  {language === 'lv' ? 'Apkalpošanas valodas' : 'Service languages'}
                </Label>
                <div className="flex flex-wrap gap-2" data-testid="shop-languages-input">
                  {[
                    { value: 'lv', lv: 'Latviešu', en: 'Latvian' },
                    { value: 'en', lv: 'Angļu', en: 'English' },
                    { value: 'ru', lv: 'Krievu', en: 'Russian' },
                    { value: 'de', lv: 'Vācu', en: 'German' },
                    { value: 'et', lv: 'Igauņu', en: 'Estonian' },
                    { value: 'lt', lv: 'Lietuviešu', en: 'Lithuanian' },
                  ].map((lang) => {
                    const active = (formData.shop_languages || []).includes(lang.value);
                    return (
                      <button
                        key={lang.value}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            shop_languages: active
                              ? (prev.shop_languages || []).filter(l => l !== lang.value)
                              : [...(prev.shop_languages || []), lang.value],
                          }));
                        }}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          active
                            ? 'bg-[#121212] text-white border-[#121212]'
                            : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'
                        }`}
                        data-testid={`lang-${lang.value}`}
                      >
                        {language === 'lv' ? lang.lv : lang.en}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Working Hours Section */}
            <div className={activeSection === 'legal' ? 'border-t border-[#E5E4E0] pt-6' : 'hidden'}>
              <h3 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D97736]" />
                {language === 'lv' ? 'Darba laiks' : 'Working hours'}
              </h3>
              <WorkingHoursPicker
                value={formData.shop_working_hours}
                onChange={(val) => setFormData(prev => ({ ...prev, shop_working_hours: val }))}
              />
              <p className="text-xs text-[#5C5C5C] mt-2">
                {language === 'lv'
                  ? 'Atzīmējiet atvērtās dienas un iestatiet darba stundas.'
                  : 'Tick open days and set working hours.'}
              </p>
            </div>

            {/* Submit */}
            {(activeSection === 'profile' || activeSection === 'legal') && (
            <Button
              type="submit"
              disabled={saving || !formData.shop_name || !formData.entity_type}
              className="w-full h-11 bg-[#D97736] hover:bg-[#C06528] text-white font-medium"
              data-testid="save-shop-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('loading')}
                </span>
              ) : verificationStatus === 'approved' ? (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {language === 'lv' ? 'Sūtīt apstiprināšanai' : 'Send for Approval'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {t('save')}
                </span>
              )}
            </Button>
            )}

            {/* Verification Section */}
            {isEditMode && activeSection === 'legal' && (
              <div className="mt-6 pt-6 border-t border-[#E5E4E0]">
                <h3 className="font-outfit font-semibold text-[#121212] mb-4 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#D97736]" />
                  {language === 'lv' ? 'Verifikācija' : 'Verification'}
                </h3>

                {verificationStatus?.is_verified ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3" data-testid="verified-badge">
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700">
                        {language === 'lv' ? 'Verificēts veikals' : 'Verified Shop'}
                      </p>
                      <p className="text-sm text-green-600">
                        {language === 'lv' ? 'Jūsu veikals ir apstiprināts' : 'Your shop has been verified'}
                      </p>
                    </div>
                  </div>
                ) : verificationStatus?.pending_request?.status === 'pending' ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3" data-testid="pending-verification">
                    <Clock className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-700">
                        {language === 'lv' ? 'Pieteikums iesniegts' : 'Request Submitted'}
                      </p>
                      <p className="text-sm text-amber-600">
                        {language === 'lv' ? 'Gaida administratora apstiprinājumu' : 'Awaiting admin approval'}
                      </p>
                    </div>
                  </div>
                ) : verificationStatus?.pending_request?.status === 'rejected' ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="rejected-verification">
                      <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-700">
                          {language === 'lv' ? 'Pieteikums noraidīts' : 'Request Rejected'}
                        </p>
                        {verificationStatus.pending_request.admin_note && (
                          <p className="text-sm text-red-600 mt-1">
                            {verificationStatus.pending_request.admin_note}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={handleRequestVerification}
                      disabled={requestingVerification}
                      variant="outline"
                      className="w-full border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white"
                      data-testid="retry-verification-btn"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Iesniegt atkārtoti' : 'Resubmit Request'}
                    </Button>
                  </div>
                ) : (
                  <div>
                    {registryCheck?.can_auto_verify ? (
                      <div className="p-4 mb-3 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3" data-testid="registry-match-hint">
                        <ShieldCheck className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-green-700">
                            {language === 'lv' ? 'Reģistrācijas numurs sakrīt ar Uzņēmumu reģistru' : 'Registration number matches the Company Register'}
                          </p>
                          <p className="text-sm text-green-600">
                            {language === 'lv'
                              ? `${registryCheck.registry_name} — veikals tiks verificēts UZREIZ.`
                              : `${registryCheck.registry_name} — your shop will be verified INSTANTLY.`}
                          </p>
                        </div>
                      </div>
                    ) : registryCheck?.duplicate ? (
                      <div className="p-4 mb-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="registry-duplicate-hint">
                        <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">
                          {language === 'lv'
                            ? 'Šis reģistrācijas numurs jau ir reģistrēts citā KoLV veikalā. Drošības dēļ viens uzņēmums var būt tikai vienam veikalam.'
                            : 'This registration number is already registered to another KoLV shop. For security, a company can belong to only one shop.'}
                        </p>
                      </div>
                    ) : registryCheck?.is_business && registryCheck?.registration_number ? (
                      <p className="text-sm text-amber-600 mb-3" data-testid="registry-nomatch-hint">
                        {language === 'lv'
                          ? 'Reģistrācijas numurs netika atrasts UR reģistrā — pieteikumu izskatīs administrators manuāli.'
                          : 'Registration number not found in the register — your request will be reviewed manually.'}
                      </p>
                    ) : null}
                    <p className="text-sm text-[#5C5C5C] mb-3">
                      {language === 'lv'
                        ? 'Iesniedziet pieteikumu, lai saņemtu verificēta veikala statusu. Tas palielina uzticamību pircēju acīs.'
                        : 'Submit a request to get verified shop status. This increases buyer trust.'}
                    </p>
                    <Button
                      type="button"
                      onClick={handleRequestVerification}
                      disabled={requestingVerification || registryCheck?.duplicate}
                      variant="outline"
                      className={registryCheck?.can_auto_verify
                        ? 'w-full border-green-600 text-green-700 hover:bg-green-600 hover:text-white'
                        : 'w-full border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white'}
                      data-testid="request-verification-btn"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      {requestingVerification
                        ? (language === 'lv' ? 'Iesniedz...' : 'Submitting...')
                        : registryCheck?.can_auto_verify
                          ? (language === 'lv' ? 'Verificēt uzreiz' : 'Verify instantly')
                          : (language === 'lv' ? 'Pieteikties verifikācijai' : 'Request Verification')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </form>
            </div>
            )}

            {/* Verification documents (Business & Legal) */}
            {isEditMode && activeSection === 'legal' && (
              <ShopDocumentsManager />
            )}

            {/* Premium Design */}
            {isEditMode && activeSection === 'design' && (
              <>
                <PremiumCustomizer />
                <CustomDomainSection language={language} />
              </>
            )}

            {/* Accounting documents + privacy/turnover threshold */}
            {activeSection === 'accounting' && (
              <>
                {isEditMode && <ComplianceWidget />}
                <AccountingDocuments />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
